import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import API from "../../helpers/Axios";
import AddAdSlotModal from "../../components/AddAdSlotModal";
import AddStudioAdModal from "../../components/AddStudioAdModal";
import AddFeaturedStudioModal from "../../components/AddFeaturedStudioModal";
import StudioAdCard from "../../components/StudioAdCard";

const normalizeList = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.items)) return payload.items;
	return [];
};

function AdsManagerScreen() {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");
	const [featuredStudios, setFeaturedStudios] = useState([]);
	const [adSlots, setAdSlots] = useState([]);
	const [studioAds, setStudioAds] = useState([]);
	const [stats, setStats] = useState(null);
	const [isAddAdSlotModalOpen, setIsAddAdSlotModalOpen] = useState(false);
	const [isAddStudioAdModalOpen, setIsAddStudioAdModalOpen] = useState(false);
	const [isAddFeaturedStudioModalOpen, setIsAddFeaturedStudioModalOpen] = useState(false);
	const [isEditFeaturedStudioModalOpen, setIsEditFeaturedStudioModalOpen] = useState(false);
	const [editingFeaturedStudio, setEditingFeaturedStudio] = useState(null);
	const [isEditStudioAdModalOpen, setIsEditStudioAdModalOpen] = useState(false);
	const [editingStudioAd, setEditingStudioAd] = useState(null);
	const [exportingAdId, setExportingAdId] = useState(null);
	const [draggedFeaturedId, setDraggedFeaturedId] = useState(null);
	const [dragInsertIndex, setDragInsertIndex] = useState(null);
	const [featuredStudioFormErrors, setFeaturedStudioFormErrors] = useState({});
	const [editFeaturedStudioFormErrors, setEditFeaturedStudioFormErrors] = useState({});
	const [studioAdFormErrors, setStudioAdFormErrors] = useState({});
	const [editStudioAdFormErrors, setEditStudioAdFormErrors] = useState({});

	const loadData = async () => {
		setIsLoading(true);
		setError("");

		try {
			const [featuredRes, slotsRes, adsRes, statsRes] = await Promise.all([
				API.get("admin/featured-studios"),
				API.get("admin/studio-ads/slots"),
				API.get("admin/studio-ads"),
				API.get("admin/studio-ads/stats"),
			]);

			setFeaturedStudios(normalizeList(featuredRes.data));
			setAdSlots(normalizeList(slotsRes.data));
			setStudioAds(normalizeList(adsRes.data));
			setStats(statsRes.data || null);
		} catch (e) {
			setError("Unable to load Ads Manager data.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		loadData();
	}, []);

	const toNumberOr = (value, fallback = 0) => {
		const n = Number(value);
		return Number.isFinite(n) ? n : fallback;
	};

	const addFeaturedStudio = async (payload) => {
		setIsSaving(true);
		setError("");
		setFeaturedStudioFormErrors({});
		try {
			const data = new FormData();
			if (payload.studio_id !== undefined && payload.studio_id !== null) {
				data.append("studio_id", toNumberOr(payload.studio_id));
			}
			data.append("active", Boolean(payload.active) ? "1" : "0");
			if (payload.image) {
				data.append("cover", payload.image);
			}

			await API.post("admin/featured-studios/create", data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setIsAddFeaturedStudioModalOpen(false);
			setFeaturedStudioFormErrors({});
			await loadData();
		} catch (e) {
			const validationErrors = e?.response?.data?.errors;
			if (validationErrors && typeof validationErrors === "object") {
				setFeaturedStudioFormErrors(validationErrors);
			} else {
				setError("Unable to add featured studio.");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const updateFeaturedStudio = async (payload) => {
		setIsSaving(true);
		setError("");
		setEditFeaturedStudioFormErrors({});
		try {
			const data = new FormData();
			data.append("id", toNumberOr(payload.id));
			if (payload.studio_id !== undefined && payload.studio_id !== null) {
				data.append("studio_id", toNumberOr(payload.studio_id));
			}
			data.append("sort_order", toNumberOr(payload.sort_order, 0));
			data.append("active", Boolean(payload.active) ? "1" : "0");
			if (payload.image) data.append("cover", payload.image);

			await API.post("admin/featured-studios/update", data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setIsEditFeaturedStudioModalOpen(false);
			setEditingFeaturedStudio(null);
			setEditFeaturedStudioFormErrors({});
			await loadData();
		} catch (e) {
			const validationErrors = e?.response?.data?.errors;
			if (validationErrors && typeof validationErrors === "object") {
				setEditFeaturedStudioFormErrors(validationErrors);
			} else {
				setError("Unable to update featured studio.");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const addAdSlot = async ({key, name, description, active}) => {
		setIsSaving(true);
		setError("");
		try {
			await API.post("admin/studio-ads/slots/create", {
				key,
				name,
				description,
				active,
			});
			setIsAddAdSlotModalOpen(false);
			await loadData();
		} catch (e) {
			setError("Unable to add ad slot.");
		} finally {
			setIsSaving(false);
		}
	};

	const addStudioAd = async (payload) => {
		setIsSaving(true);
		setError("");
		setStudioAdFormErrors({});
		try {
			const data = new FormData();
			data.append("studio_id", payload.studio_id ? toNumberOr(payload.studio_id) : "");
			data.append("slot_id", toNumberOr(payload.slot_id));
			(payload.slot_ids || []).forEach((slotId) => data.append("slot_ids[]", toNumberOr(slotId)));
			data.append("title", payload.title);
			data.append("destination_url", payload.destination_url || "");
			data.append("cover", payload.cover);
			data.append("priority", toNumberOr(payload.priority, 10));
			data.append("sort_order", toNumberOr(payload.sort_order, 1));
			data.append("display_seconds", toNumberOr(payload.display_seconds, 10));
			data.append("budget_impressions", toNumberOr(payload.budget_impressions, 0));
			data.append("budget_clicks", toNumberOr(payload.budget_clicks, 0));
			data.append("active", payload.active ? "1" : "0");

			await API.post("admin/studio-ads/create", data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setIsAddStudioAdModalOpen(false);
			setStudioAdFormErrors({});
			await loadData();
		} catch (e) {
			const validationErrors = e?.response?.data?.errors;
			if (validationErrors && typeof validationErrors === "object") {
				setStudioAdFormErrors(validationErrors);
			} else {
				setError("Unable to add studio ad.");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const toggleStudioAd = async (item) => {
		const isActive = Boolean(item.active ?? item.is_active);
		setIsSaving(true);
		setError("");
		try {
			await API.post("admin/studio-ads/update", {
				id: item.id,
				title: item.title || "",
				priority: item.priority ?? 0,
				sort_order: item.sort_order ?? 0,
				active: !isActive,
			});
			await loadData();
		} catch (e) {
			setError("Unable to update studio ad.");
		} finally {
			setIsSaving(false);
		}
	};

	const updateStudioAd = async (payload) => {
		setIsSaving(true);
		setError("");
		setEditStudioAdFormErrors({});
		try {
			const data = new FormData();
			data.append("id", toNumberOr(payload.id));
			data.append("studio_id", payload.studio_id ? toNumberOr(payload.studio_id) : "");
			data.append("slot_id", toNumberOr(payload.slot_id));
			(payload.slot_ids || []).forEach((slotId) => data.append("slot_ids[]", toNumberOr(slotId)));
			data.append("title", payload.title);
			data.append("destination_url", payload.destination_url || "");
			if (payload.cover) data.append("cover", payload.cover);
			data.append("priority", toNumberOr(payload.priority, 10));
			data.append("sort_order", toNumberOr(payload.sort_order, 1));
			data.append("display_seconds", toNumberOr(payload.display_seconds, 10));
			data.append("budget_impressions", toNumberOr(payload.budget_impressions, 0));
			data.append("budget_clicks", toNumberOr(payload.budget_clicks, 0));
			data.append("active", payload.active ? "1" : "0");

			await API.post("admin/studio-ads/update", data, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			setIsEditStudioAdModalOpen(false);
			setEditingStudioAd(null);
			setEditStudioAdFormErrors({});
			await loadData();
		} catch (e) {
			const validationErrors = e?.response?.data?.errors;
			if (validationErrors && typeof validationErrors === "object") {
				setEditStudioAdFormErrors(validationErrors);
			} else {
				setError("Unable to update studio ad.");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const exportAdCsv = async (item) => {
		setError("");
		setExportingAdId(item.id);
		try {
			const response = await API.get("ads/export", {
				params: {ad_id: item.id},
				responseType: "blob",
			});
			const header = response?.headers?.["content-disposition"] || "";
			const filenameMatch = header.match(/filename="?([^"]+)"?/i);
			const filename = filenameMatch?.[1] || `ads_export_ad_${item.id}.csv`;
			const blob = new Blob([response.data], {type: "text/csv;charset=utf-8;"});
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", filename);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (e) {
			setError("Unable to export ad CSV.");
		} finally {
			setExportingAdId(null);
		}
	};

	const toggleFeatured = async (item) => {
		setIsSaving(true);
		try {
			await API.post("admin/featured-studios/update", {
				id: item.id,
				sort_order: item.sort_order ?? 0,
				active: !(item.active ?? item.is_active),
			});
			await loadData();
		} catch (e) {
			setError("Unable to update featured studio.");
		} finally {
			setIsSaving(false);
		}
	};

	const deleteFeatured = async (item) => {
		const studioName = item.studio?.name || item.studio_name || item.title || "this studio";
		const confirmed = window.confirm(
			`Delete featured studio #${item.id} (${studioName})?`
		);
		if (!confirmed) return;

		setIsSaving(true);
		setError("");
		try {
			await API.post("admin/featured-studios/delete", {
				id: item.id,
			});
			await loadData();
		} catch (e) {
			setError("Unable to delete featured studio.");
		} finally {
			setIsSaving(false);
		}
	};

	const toggleSlot = async (item) => {
		setIsSaving(true);
		try {
			await API.post("admin/studio-ads/slots/update", {
				id: item.id,
				key: item.key,
				name: item.name,
				description: item.description || "",
				active: !(item.active ?? item.is_active),
			});
			await loadData();
		} catch (e) {
			setError("Unable to update ad slot.");
		} finally {
			setIsSaving(false);
		}
	};

	const deleteStudioAd = async (item) => {
		const confirmed = window.confirm(`Delete studio ad #${item.id} (${item.title || "Untitled ad"})?`);
		if (!confirmed) return;

		setIsSaving(true);
		setError("");
		try {
			await API.post("admin/studio-ads/delete", {
				id: item.id,
			});
			await loadData();
		} catch (e) {
			setError("Unable to delete studio ad.");
		} finally {
			setIsSaving(false);
		}
	};

	const statusPill = (isActive) => (
		<span
			className={`rounded-full px-3 py-1 text-xs font-semibold ${
				isActive ? "bg-[#E8F8EF] text-[#198754]" : "bg-[#F3F4F6] text-[#6D6E6F]"
			}`}
		>
			{isActive ? "Active" : "Inactive"}
		</span>
	);

	const mobileToggle = (isActive, onClick) => (
		<button
			type="button"
			onClick={onClick}
			disabled={isSaving}
			className={`relative inline-flex h-7 w-12 items-center rounded-full transition disabled:opacity-60 ${
				isActive ? "bg-[#31A762]" : "bg-[#D1D5DB]"
			}`}
			aria-pressed={isActive}
		>
			<span
				className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
					isActive ? "translate-x-6" : "translate-x-1"
				}`}
			/>
		</button>
	);

	const featuredCoverUrl = (item) =>
		item.cover_url ||
		item.studio?.artworks?.[0]?.url ||
		item.studio?.artworks?.[0]?.image_url ||
		item.studio?.images?.[0]?.url ||
		item.studio?.images?.[0]?.image_url ||
		item.studio?.artwork_url ||
		"";

	const reorderFeaturedStudios = (list, draggedId, insertIndexRaw) => {
		const fromIndex = list.findIndex((item) => item.id === draggedId);
		if (fromIndex < 0) return list;
		let insertIndex = insertIndexRaw;
		if (!Number.isFinite(insertIndex)) return list;
		if (fromIndex < insertIndex) insertIndex -= 1;
		insertIndex = Math.max(0, Math.min(insertIndex, list.length - 1));
		if (fromIndex === insertIndex) return list;
		const next = [...list];
		const [moved] = next.splice(fromIndex, 1);
		next.splice(insertIndex, 0, moved);
		return next;
	};

	const resolveFeaturedInsertIndexFromCard = (event, cardIndex) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const midpoint = rect.top + rect.height / 2;
		return event.clientY < midpoint ? cardIndex : cardIndex + 1;
	};

	const handleFeaturedDrop = async (insertIndex) => {
		if (!draggedFeaturedId) return;
		const next = reorderFeaturedStudios(featuredStudios, draggedFeaturedId, insertIndex);
		setDraggedFeaturedId(null);
		setDragInsertIndex(null);
		const currentOrder = featuredStudios.map((entry) => entry.id).join(",");
		const nextOrder = next.map((entry) => entry.id).join(",");
		if (currentOrder === nextOrder) return;
		setFeaturedStudios(next);
		await persistFeaturedSortOrder(next);
	};

	const setFeaturedDragPreview = (event, label) => {
		const preview = document.createElement("div");
		preview.textContent = label || "Featured studio";
		preview.style.position = "fixed";
		preview.style.top = "12px";
		preview.style.left = "12px";
		preview.style.padding = "6px 10px";
		preview.style.border = "1px solid #31A762";
		preview.style.borderRadius = "8px";
		preview.style.background = "#FFFFFF";
		preview.style.color = "#1E1F21";
		preview.style.fontSize = "12px";
		preview.style.fontWeight = "600";
		preview.style.boxShadow = "0 8px 20px rgba(0,0,0,0.15)";
		preview.style.pointerEvents = "none";
		preview.style.zIndex = "9999";
		document.body.appendChild(preview);
		event.dataTransfer.setDragImage(preview, 8, 8);
		requestAnimationFrame(() => {
			if (preview.parentNode) preview.parentNode.removeChild(preview);
		});
	};

	const persistFeaturedSortOrder = async (ordered) => {
		setIsSaving(true);
		setError("");
		try {
			await Promise.all(
				ordered.map((item, index) =>
					API.post("admin/featured-studios/update", {
						id: item.id,
						sort_order: index,
						active: Boolean(item.active ?? item.is_active),
					})
				)
			);
			await loadData();
		} catch (e) {
			setError("Unable to reorder featured studios.");
			await loadData();
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className="flex-1 p-10">
			<AddFeaturedStudioModal
				isOpen={isAddFeaturedStudioModalOpen}
				isSaving={isSaving}
				onClose={() => {
					setIsAddFeaturedStudioModalOpen(false);
					setFeaturedStudioFormErrors({});
				}}
				onSubmit={addFeaturedStudio}
				serverErrors={featuredStudioFormErrors}
				onClearServerErrors={() => setFeaturedStudioFormErrors({})}
			/>
			<AddFeaturedStudioModal
				isOpen={isEditFeaturedStudioModalOpen}
				isSaving={isSaving}
				onClose={() => {
					setIsEditFeaturedStudioModalOpen(false);
					setEditingFeaturedStudio(null);
					setEditFeaturedStudioFormErrors({});
				}}
				onSubmit={updateFeaturedStudio}
				initialValues={editingFeaturedStudio}
				modalTitle="Edit featured studio"
				modalDescription="Update featured studio details."
				submitLabel="Save changes"
				requireCover={false}
				serverErrors={editFeaturedStudioFormErrors}
				onClearServerErrors={() => setEditFeaturedStudioFormErrors({})}
			/>
			<AddAdSlotModal
				isOpen={isAddAdSlotModalOpen}
				isSaving={isSaving}
				onClose={() => setIsAddAdSlotModalOpen(false)}
				onSubmit={addAdSlot}
			/>
			<AddStudioAdModal
				isOpen={isAddStudioAdModalOpen}
				isSaving={isSaving}
				onClose={() => {
					setIsAddStudioAdModalOpen(false);
					setStudioAdFormErrors({});
				}}
				onSubmit={addStudioAd}
				serverErrors={studioAdFormErrors}
				onClearServerErrors={() => setStudioAdFormErrors({})}
			/>
			<AddStudioAdModal
				isOpen={isEditStudioAdModalOpen}
				isSaving={isSaving}
				onClose={() => {
					setIsEditStudioAdModalOpen(false);
					setEditingStudioAd(null);
					setEditStudioAdFormErrors({});
				}}
				onSubmit={updateStudioAd}
				modalTitle="Add ad"
				modalDescription="Create or update ad details."
				submitLabel="Save changes"
				initialValues={editingStudioAd}
				requireCover={false}
				serverErrors={editStudioAdFormErrors}
				onClearServerErrors={() => setEditStudioAdFormErrors({})}
			/>

			<div className="mb-8 flex flex-col items-start justify-between gap-4 xl:flex-row xl:items-center">
				<div>
					<h1 className="text-5xl font-semibold">Ads Manager</h1>
					<p className="mt-4 text-xl text-[#595959]">
						Manage featured studios, ad slots, and studio ads from Admin API.
					</p>
				</div>
			</div>

			{error ? (
				<div className="mb-6 rounded-md border border-[#F5C2C7] bg-[#FBEAEC] px-4 py-3 text-sm text-[#842029]">
					{error}
				</div>
			) : null}

			<div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
				{[
					{
						title: "Featured Studios",
						value: featuredStudios.length,
						icon: (
							<svg width={18} height={18} fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M9 2 2.5 5.2v4.6C2.5 13.9 5.2 16.9 9 18c3.8-1.1 6.5-4.1 6.5-8.2V5.2L9 2Z" stroke="#58B17D" strokeWidth={1.5} strokeLinejoin="round" />
							</svg>
						),
					},
					{
						title: "Ad Slots",
						value: adSlots.length,
						icon: (
							<svg width={18} height={18} fill="none" xmlns="http://www.w3.org/2000/svg">
								<rect x={2} y={3} width={14} height={12} rx={2} stroke="#58B17D" strokeWidth={1.5} />
								<path d="M5 6h8M5 9h5" stroke="#58B17D" strokeWidth={1.5} strokeLinecap="round" />
							</svg>
						),
					},
					{
						title: "Ads",
						value: studioAds.length,
						icon: (
							<svg width={18} height={18} fill="none" xmlns="http://www.w3.org/2000/svg">
								<rect x={2} y={3} width={14} height={12} rx={2} stroke="#58B17D" strokeWidth={1.5} />
								<path d="m7 7 4 2-4 2V7Z" fill="#58B17D" />
							</svg>
						),
					},
					{
						title: "Impressions",
						value: stats?.summary?.impressions ?? stats?.impressions ?? 0,
						icon: (
							<svg width={18} height={18} fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M1.5 9s2.4-4 7.5-4 7.5 4 7.5 4-2.4 4-7.5 4-7.5-4-7.5-4Z" stroke="#58B17D" strokeWidth={1.5} strokeLinejoin="round" />
								<circle cx={9} cy={9} r={2} stroke="#58B17D" strokeWidth={1.5} />
							</svg>
						),
					},
				].map((card) => (
					<div key={card.title} className="rounded-lg bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
						<div className="inline-flex border-b-2 border-[#096730] px-4">
							<p className="text-sm font-semibold leading-10 text-[#096730]">{card.title}</p>
						</div>
						<div className="flex items-center justify-between px-4 pb-4 pt-3">
							<p className="text-[28px] font-bold text-[#1E1F21]">{card.value}</p>
							<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EDFDF4]">
								{card.icon}
							</div>
						</div>
					</div>
				))}
			</div>

			<div className="space-y-6">
				<div>
					<div className="mb-2 flex items-center justify-between gap-3">
						<h2 className="text-xl font-semibold text-[#1E1F21]">Featured Studios</h2>
						<button
							type="button"
							onClick={() => setIsAddFeaturedStudioModalOpen(true)}
							disabled={isSaving}
							className="inline-flex items-center gap-2 rounded-md border border-[#E4E1E0] bg-white px-4 py-2 text-sm font-semibold text-[#1E1F21] transition hover:bg-[#F8F8F8] disabled:opacity-60"
						>
							<svg width={16} height={16} fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M8 3.333v9.334M3.333 8h9.334" stroke="#1E1F21" strokeWidth={1.5} strokeLinecap="round" />
							</svg>
							Create
						</button>
					</div>
					<section className="rounded-lg bg-white px-6 py-2 shadow-[0_1px_8px_rgba(0,0,0,0.06)]">
						<div>
							{featuredStudios.map((item, index) => {
								const isActive = Boolean(item.active ?? item.is_active);
								const displayTitle = item.studio?.name || item.studio_name || item.title || "Untitled studio";
								const coverUrl = featuredCoverUrl(item);
								return (
									<div key={item.id}>
										<div
											onDragOver={(event) => {
												event.preventDefault();
												if (!draggedFeaturedId) return;
												setDragInsertIndex(index);
											}}
											onDrop={async () => {
												await handleFeaturedDrop(index);
											}}
											className="relative h-2"
										>
											{dragInsertIndex === index && draggedFeaturedId ? (
												<div className="absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-[#31A762]" />
											) : null}
										</div>

										<div
											draggable
											onDragStart={(event) => {
												setFeaturedDragPreview(event, displayTitle);
												setDraggedFeaturedId(item.id);
											}}
											onDragOver={(event) => {
												event.preventDefault();
												if (!draggedFeaturedId) return;
												setDragInsertIndex(resolveFeaturedInsertIndexFromCard(event, index));
											}}
											onDrop={async (event) => {
												event.preventDefault();
												const insertIndex = resolveFeaturedInsertIndexFromCard(event, index);
												await handleFeaturedDrop(insertIndex);
											}}
											onDragEnd={() => {
												setDraggedFeaturedId(null);
												setDragInsertIndex(null);
											}}
											className={`relative overflow-hidden rounded-lg border bg-white shadow-[0_4px_14px_rgba(0,0,0,0.04)] ${
												draggedFeaturedId === item.id ? "border-[#31A762]" : "border-[#E8E8E8]"
											}`}
										>
											<div className="flex items-center">
												<div
													className="flex h-14 w-10 flex-shrink-0 items-center justify-center border-r border-[#ECECEC] bg-[#F8F9FA] text-[#8B8B8B]"
													title="Drag to reorder"
												>
													⋮⋮
												</div>

												<div className="h-14 w-24 flex-shrink-0 bg-[#EEF0F2]">
													{coverUrl ? (
														<img src={coverUrl} alt={displayTitle} className="h-full w-full object-cover" />
													) : (
														<div className="flex h-full w-full items-center justify-center text-xs font-medium text-[#6D6E6F]">
															No cover
														</div>
													)}
												</div>

												<div className="flex min-w-0 flex-1 items-center justify-between gap-3 px-3 py-2">
													<div className="min-w-0">
														<p className="truncate text-sm font-semibold text-[#1E1F21]">{displayTitle}</p>
														<p className="mt-0.5 truncate text-xs text-[#6D6E6F]">
															ID: {item.id} • Studio: {item.studio_id} • Sort: {item.sort_order ?? 0}
														</p>
													</div>

													<div className="flex flex-shrink-0 items-center gap-2">
														{mobileToggle(isActive, () => toggleFeatured(item))}
														<button
															type="button"
															onClick={() => {
																setEditingFeaturedStudio(item);
																setEditFeaturedStudioFormErrors({});
																setIsEditFeaturedStudioModalOpen(true);
															}}
															disabled={isSaving}
															className="rounded-md border border-[#D5D5D5] px-2.5 py-1 text-xs font-medium text-[#6D6E6F] transition hover:bg-[#F8F8F8] disabled:opacity-60"
														>
															Edit
														</button>

														<button
															type="button"
															onClick={() => deleteFeatured(item)}
															disabled={isSaving}
															className="rounded-md border border-[#DC3545] bg-white px-2.5 py-1 text-xs font-semibold text-[#DC3545] transition hover:bg-[#FDF2F4] disabled:opacity-60"
														>
															Delete
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								);
							})}
							<div
								onDragOver={(event) => {
									event.preventDefault();
									if (!draggedFeaturedId) return;
									setDragInsertIndex(featuredStudios.length);
								}}
								onDrop={async () => {
									await handleFeaturedDrop(featuredStudios.length);
								}}
								className="relative h-3"
							>
								{dragInsertIndex === featuredStudios.length && draggedFeaturedId ? (
									<div className="absolute left-0 right-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-[#31A762]" />
								) : null}
							</div>
							{featuredStudios.length === 0 && !isLoading ? (
								<p className="text-sm text-[#6D6E6F]">No featured studios found.</p>
							) : null}
						</div>
					</section>
				</div>
				<div>
					<div className="mb-2 flex items-center justify-between gap-3">
						<h2 className="text-xl font-semibold text-[#1E1F21]">Ad Banners</h2>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setIsAddStudioAdModalOpen(true)}
								disabled={isSaving}
								className="inline-flex items-center gap-2 rounded-md border border-[#E4E1E0] bg-white px-4 py-2 text-sm font-semibold text-[#1E1F21] transition hover:bg-[#F8F8F8] disabled:opacity-60"
							>
								<svg width={16} height={16} fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M8 3.333v9.334M3.333 8h9.334" stroke="#1E1F21" strokeWidth={1.5} strokeLinecap="round" />
								</svg>
								Create
							</button>
							<button
								type="button"
								onClick={() => navigate("/studio-ads")}
								className="inline-flex items-center gap-2 rounded-md border border-[#E4E1E0] bg-white px-4 py-2 text-sm font-semibold text-[#1E1F21] transition hover:bg-[#F8F8F8]"
							>
								<svg width={16} height={16} fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M3.333 4.667a.667.667 0 0 0-.666.667v7.333a.667.667 0 0 0 .666.667h7.334a.667.667 0 0 0 .666-.667v-4"
										stroke="#1E1F21"
										strokeWidth={1.5}
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
									<path d="M9.333 2h4v4M13.333 2l-6.667 6.667" stroke="#1E1F21" strokeWidth={1.5} strokeLinecap="round" />
								</svg>
								View all
							</button>
						</div>
					</div>
					<section className="rounded-lg bg-white p-6 shadow-[0_1px_8px_rgba(0,0,0,0.06)]">
						<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
							{studioAds.slice(0, 4).map((item) => (
								<StudioAdCard
									key={item.id}
									item={item}
									isSaving={isSaving}
									isExporting={exportingAdId === item.id}
									onToggle={() => toggleStudioAd(item)}
									onEdit={() => {
										setEditingStudioAd(item);
										setEditStudioAdFormErrors({});
										setIsEditStudioAdModalOpen(true);
									}}
									onDelete={() => deleteStudioAd(item)}
									onExport={() => exportAdCsv(item)}
								/>
							))}
							{studioAds.length === 0 && !isLoading ? <p className="text-sm text-[#6D6E6F]">No studio ads yet.</p> : null}
						</div>
					</section>
				</div>
				<div>
					<div className="mb-2 flex items-center justify-between gap-3">
						<h2 className="text-xl font-semibold text-[#1E1F21]">Ad Slots</h2>
						<button
							type="button"
							onClick={() => setIsAddAdSlotModalOpen(true)}
							disabled={isSaving}
							className="inline-flex items-center gap-2 rounded-md border border-[#E4E1E0] bg-white px-4 py-2 text-sm font-semibold text-[#1E1F21] transition hover:bg-[#F8F8F8] disabled:opacity-60"
						>
							<svg width={16} height={16} fill="none" xmlns="http://www.w3.org/2000/svg">
								<rect x={2.5} y={3.5} width={11} height={9} rx={1.5} stroke="#1E1F21" strokeWidth={1.5} />
								<path d="M8 1.833v3.334M6.333 3.5h3.334" stroke="#1E1F21" strokeWidth={1.5} strokeLinecap="round" />
							</svg>
							Add ad slot
						</button>
					</div>
					<div>
						<section className="rounded-lg bg-white p-6 shadow-[0_1px_8px_rgba(0,0,0,0.06)]">
							<div className="space-y-3">
								{adSlots.slice(0, 8).map((item) => {
									const isActive = Boolean(item.active ?? item.is_active);
									return (
										<div key={item.id} className="flex items-center justify-between rounded border border-[#ECECEC] px-4 py-3">
											<div>
												<p className="font-semibold text-[#1E1F21]">{item.name || item.key || `Slot #${item.id}`}</p>
												<p className="text-sm text-[#6D6E6F]">{item.key ? `Key: ${item.key}` : `ID: ${item.id}`}</p>
											</div>
											<div className="flex items-center gap-3">
												{statusPill(isActive)}
												{mobileToggle(isActive, () => toggleSlot(item))}
											</div>
										</div>
									);
								})}
								{adSlots.length === 0 && !isLoading ? (
									<p className="text-sm text-[#6D6E6F]">No ad slots found.</p>
								) : null}
							</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	);
}

export default AdsManagerScreen;
